/*
  Discount code controls: top bar, home page hero, product page line.

  Any element with data-promo-code is a code control. A tap on one:
    1. copies the code, inside the tap and before anything is awaited
       (Safari refuses a clipboard write that follows a network call),
    2. confirms it on the control (.is-copied) and in one message that is
       seen and is read out politely (snippets/waltons-promo-code.liquid),
    3. when Theme settings allow it, puts the code on the cart in the
       background, so it is already there at checkout.

  Copying is the certain part. Step 3 is an extra. Every failure in it is
  swallowed: nothing is shown, nothing is blocked, and the customer keeps
  the copied code to type in at checkout.

  Other attributes on a control:
    data-promo-swaps        the other codes of the same promotion, comma
                            separated. They do not combine with this one,
                            so the cart keeps whichever saves more and
                            the other comes off. Nothing that is saving
                            money is ever taken off first (see putOnCart).
    data-promo-link         an address added to the message as a link, for
    data-promo-link-label   an offer that belongs to one range.

  Any element with data-promo-until (epoch milliseconds) is hidden once
  that moment has passed, because a cached page can outlive its promotion.
*/
(function () {
  'use strict';

  // Once per page, however many times the file is loaded. Twice would
  // confirm every tap twice and write to the cart twice.
  if (window.WaltonsPromoCode) return;
  window.WaltonsPromoCode = true;

  var WORDS = {
    copied: '{code} copied.',
    notCopied: 'Your code is {code}.',
    typeIt: 'Type it in at checkout.',
    added: 'Code applied to your cart.',
    swapped: 'Code applied to your cart in place of {other}.',
    keptOther: 'Your cart saves more with {other}, so that stays.',
    already: 'It is already on your cart.'
  };

  var COPIED_FOR = 2500;
  var MESSAGE_FOR = 6000;
  var MESSAGE_WITH_LINK_FOR = 9000;
  var REQUEST_LIMIT = 4000;
  // How long the spoken message waits for the cart, so that one sentence
  // is read once and not a short one followed by a longer one.
  var SPEAK_AFTER = 1500;

  var box = null;
  var live = null;
  var parts = null;
  var hideTimer = null;
  var clearTimer = null;
  var liveTimer = null;
  var held = false;
  var liftTimer = null;
  var current = null;
  var applyChain = Promise.resolve();
  var waiting = null;

  function words(key, values) {
    return WORDS[key].replace(/\{(\w+)\}/g, function (match, name) {
      return values && values[name] != null ? values[name] : '';
    });
  }

  function same(a, b) {
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  }

  function has(list, code) {
    for (var i = 0; i < list.length; i++) {
      if (same(list[i], code)) return true;
    }
    return false;
  }

  function listFrom(value) {
    return (value || '').split(',').map(function (item) { return item.trim(); }).filter(Boolean);
  }

  /* ---------- the message ---------- */

  function setUpMessage() {
    box = document.getElementById('WaltonsPromoStatus');
    live = document.getElementById('WaltonsPromoLive');
    if (!box) {
      box = document.createElement('div');
      box.id = 'WaltonsPromoStatus';
      box.className = 'wpc-status';
      document.body.appendChild(box);
    }
    if (!live) {
      // The snippet is missing from the layout. A region made here still
      // exists long before the first tap, which is what a screen reader
      // needs in order to announce it.
      live = document.createElement('div');
      live.id = 'WaltonsPromoLive';
      live.className = 'wpc-live';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      // display is forced because the theme hides every empty div, and a
      // region that is not drawn while empty is not announced when filled.
      live.style.cssText = 'display:block !important;position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
      document.body.appendChild(live);
    }

    var text = document.createElement('p');
    text.className = 'wpc-status__text';
    var link = document.createElement('a');
    link.className = 'wpc-status__link';
    link.hidden = true;
    parts = { text: text, link: link };
    box.appendChild(text);
    box.appendChild(link);

    var hold = function () { held = true; clearTimeout(hideTimer); };
    var release = function () { held = false; scheduleHide(); };
    box.addEventListener('mouseenter', hold);
    box.addEventListener('mouseleave', release);
    box.addEventListener('focusin', hold);
    box.addEventListener('focusout', release);
    // A tap on the message itself puts it away. On a touch screen that is
    // also what stops a touched message waiting for a pointer to leave.
    box.addEventListener('click', function (event) {
      if (!parts.link.contains(event.target)) hideMessage();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && box.classList.contains('is-open')) hideMessage();
    });
  }

  // The dock on phones, the sticky buy card and Shopify's own bar on
  // unpublished themes all sit on the bottom edge. The message rides
  // above whichever of them is showing.
  function liftAboveFixedBars() {
    var lift = 0;
    var height = window.innerHeight;
    ['.wmb', '.product-sticky-form__card', '#PBarNextFrameWrapper'].forEach(function (selector) {
      var bar = document.querySelector(selector);
      if (!bar) return;
      var style = window.getComputedStyle(bar);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      var edge = bar.getBoundingClientRect();
      if (edge.height === 0 || edge.top < height / 2 || edge.top >= height) return;
      lift = Math.max(lift, height - edge.top);
    });
    box.style.setProperty('--wpc-lift', Math.round(lift) + 'px');
  }

  function resultFor(state) {
    if (state.outcome === 'added') return words('added', state);
    if (state.outcome === 'swapped') return words('swapped', state);
    if (state.outcome === 'keptOther') return words('keptOther', state);
    if (state.outcome === 'already') return words('already', state);
    return state.copied ? '' : words('typeIt', state);
  }

  function messageFor(state) {
    return [words(state.copied ? 'copied' : 'notCopied', state), resultFor(state)].filter(Boolean).join(' ');
  }

  function showMessage(state) {
    if (!box || current !== state) return;
    clearTimeout(clearTimer);
    parts.text.textContent = messageFor(state);
    if (state.link && state.linkLabel) {
      parts.link.href = state.link;
      parts.link.textContent = state.linkLabel;
      parts.link.hidden = false;
    } else {
      parts.link.hidden = true;
    }
    liftAboveFixedBars();
    // The buy card slides in and out as the page scrolls, so an open
    // message keeps checking what is under it.
    clearInterval(liftTimer);
    liftTimer = setInterval(liftAboveFixedBars, 400);
    box.classList.add('is-open');
    scheduleHide();
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    if (held || !current) return;
    hideTimer = setTimeout(hideMessage, current.link ? MESSAGE_WITH_LINK_FOR : MESSAGE_FOR);
  }

  function hideMessage() {
    clearTimeout(hideTimer);
    clearInterval(liftTimer);
    if (!box) return;
    var state = current;
    current = null;
    held = false;
    // Focus can only be in here on the message's link. Hand it back to the
    // control that was tapped before the link goes.
    if (box.contains(document.activeElement) && state && document.contains(state.control)) {
      try { state.control.focus({ preventScroll: true }); } catch (error) {}
    }
    box.classList.remove('is-open');
    // The words stay until the fade is over, so the box does not shrink
    // on its way out. The spoken copy goes too, so nothing stale is left
    // for a screen reader to find later.
    clearTimeout(clearTimer);
    clearTimer = setTimeout(function () {
      if (current) return;
      parts.text.textContent = '';
      parts.link.hidden = true;
      parts.link.textContent = '';
      parts.link.removeAttribute('href');
      clearTimeout(liveTimer);
      if (live) live.textContent = '';
    }, 240);
  }

  function speak(text) {
    if (!live || !text) return;
    // Emptied first: a region that is given the words it already holds is
    // not read again, and a second tap on the same code must be.
    clearTimeout(liveTimer);
    live.textContent = '';
    liveTimer = setTimeout(function () { live.textContent = text; }, 80);
  }

  function speakOnce(state) {
    if (state.spoken || current !== state) return;
    state.spoken = true;
    clearTimeout(state.speakTimer);
    speak(messageFor(state));
  }

  /* ---------- copy ---------- */

  function copyWithSelection(code) {
    var field = document.createElement('textarea');
    var back = document.activeElement;
    var copied = false;
    try {
      field.value = code;
      field.setAttribute('readonly', '');
      field.setAttribute('aria-hidden', 'true');
      field.tabIndex = -1;
      // 16px stops iOS zooming in while the field holds focus.
      field.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0;font-size:16px;';
      document.body.appendChild(field);
      field.focus({ preventScroll: true });
      field.select();
      field.setSelectionRange(0, code.length);
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    if (field.parentNode) field.parentNode.removeChild(field);
    // The field took focus. Put it back where the customer was.
    if (back && back !== document.body && typeof back.focus === 'function') {
      try { back.focus({ preventScroll: true }); } catch (error) {}
    }
    return copied;
  }

  function copy(code) {
    return new Promise(function (resolve) {
      var settled = false;
      var settle = function (copied) {
        if (settled) return;
        settled = true;
        resolve(copied);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(
            function () { settle(true); },
            function () { settle(copyWithSelection(code)); }
          );
          // A browser that never answers must not leave the tap unconfirmed.
          setTimeout(function () { if (!settled) settle(copyWithSelection(code)); }, 1200);
          return;
        }
      } catch (error) {}
      settle(copyWithSelection(code));
    });
  }

  function flash(control) {
    control.classList.add('is-copied');
    clearTimeout(control.wpcTimer);
    control.wpcTimer = setTimeout(function () { control.classList.remove('is-copied'); }, COPIED_FOR);
  }

  /* ---------- cart ---------- */

  function settings() {
    var data = box ? box.dataset : {};
    return {
      apply: data.apply === 'true' && typeof window.fetch === 'function',
      cartUrl: (data.cartUrl || '/cart') + '.js',
      updateUrl: (data.updateUrl || '/cart/update') + '.js'
    };
  }

  // Gives up after REQUEST_LIMIT whether or not the browser can cancel
  // the request, so one that never answers cannot hold up the next tap.
  function request(url, options) {
    options = options || {};
    options.credentials = 'same-origin';
    options.headers = options.headers || {};
    options.headers.Accept = 'application/json';
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) options.signal = controller.signal;

    return new Promise(function (resolve, reject) {
      var limit = setTimeout(function () {
        reject(new Error('promo code request timed out'));
        if (controller) { try { controller.abort(); } catch (error) {} }
      }, REQUEST_LIMIT);

      fetch(url, options).then(function (response) {
        if (!response.ok) throw new Error('promo code request ' + response.status);
        return response.json();
      }).then(
        function (answer) { clearTimeout(limit); resolve(answer); },
        function (error) { clearTimeout(limit); reject(error); }
      );
    });
  }

  function readCart(config) {
    return request(config.cartUrl, { method: 'GET' });
  }

  function writeCodes(config, codes) {
    return request(config.updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
  }

  function looksLikeCart(cart) {
    return !!cart && typeof cart === 'object' && typeof cart.item_count === 'number' && typeof cart.total_discount === 'number';
  }

  // discount_codes ([{ code, applicable }]) lists every code on the cart,
  // working or not. Shopify's own themes read it, but the Cart API
  // reference does not list it. Without it a code that is on the cart and
  // doing nothing cannot be seen, and a write would drop it, so an answer
  // without the list is never acted on: the customer keeps the copied
  // code and the cart is left alone.
  function usable(cart) {
    return looksLikeCart(cart) && Array.isArray(cart.discount_codes);
  }

  function codesOn(cart) {
    var list = [];
    cart.discount_codes.forEach(function (entry) {
      if (entry && typeof entry.code === 'string' && entry.code && !has(list, entry.code)) list.push(entry.code);
    });
    return list;
  }

  // The codes behind the cart's discounts, from the documented parts of
  // the cart: a discount application of type discount_code is titled with
  // its code.
  function codesAtWork(cart) {
    var list = [];
    var take = function (application) {
      if (application && application.type === 'discount_code' && typeof application.title === 'string' && application.title && !has(list, application.title)) {
        list.push(application.title);
      }
    };
    (cart.cart_level_discount_applications || []).forEach(take);
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        take(allocation && allocation.discount_application);
      });
    });
    return list;
  }

  // Taking money off right now. Shopify's flag has to say so, and when
  // the cart names the codes behind its discounts this has to be one of
  // them: two codes that cannot be combined may both fit a cart while
  // only the better one is used.
  function isWorking(cart, code) {
    var flagged = cart.discount_codes.some(function (entry) {
      return entry && typeof entry.code === 'string' && same(entry.code, code) && entry.applicable === true;
    });
    if (!flagged) return false;
    var atWork = codesAtWork(cart);
    return !atWork.length || has(atWork, code);
  }

  // The drawer and the cart page address lines by line key, and keys
  // change when discounts change, so both are redrawn after any write.
  function redrawCart() {
    var targets = [];
    [['cart-drawer[data-section-id]', 'MiniCart-'], ['main-cart[data-section-id]', 'MainCart-']].forEach(function (pair) {
      var owner = document.querySelector(pair[0]);
      if (!owner) return;
      var section = owner.getAttribute('data-section-id');
      if (section && document.getElementById(pair[1] + section)) targets.push({ section: section, id: pair[1] + section });
    });
    if (!targets.length) return Promise.resolve();

    var address = window.location.pathname + '?sections=' + targets.map(function (target) { return encodeURIComponent(target.section); }).join(',');
    return request(address, { method: 'GET' }).then(function (sections) {
      targets.forEach(function (target) {
        var html = sections && sections[target.section];
        var old = document.getElementById(target.id);
        if (typeof html !== 'string' || !old) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(target.id);
        if (fresh) old.innerHTML = fresh.innerHTML;
      });
    }).catch(function () {});
  }

  // Resolves with what happened (added, swapped, keptOther, already or
  // none) and whether the cart views need redrawing. Never rejects.
  //
  // A tap must never leave the cart saving less than before it, whatever
  // fails on the way. So a code that may be saving money is never taken
  // off first. The tapped code goes on beside what is already there,
  // Shopify uses whichever saves more, and only then does the one doing
  // nothing come off. If a request is lost at any step, or the customer
  // leaves the page, the cart is left holding both and still gets the
  // better one.
  function putOnCart(config, code, swaps) {
    var wrote = false;
    var hadItems = false;

    var write = function (codes) {
      wrote = true;
      return writeCodes(config, codes);
    };
    // The cart after a write, or null when the write was lost or its
    // answer cannot be read.
    var settled = function (writing) {
      return writing.then(function (cart) { return usable(cart) ? cart : null; }, function () { return null; });
    };
    // Only for an answer that makes no sense. Tried twice.
    var restore = function (codes) {
      return write(codes).catch(function () { return write(codes); }).catch(function () {});
    };
    var done = function (outcome, other) {
      return { outcome: outcome, other: other || '' };
    };

    return readCart(config).then(function (before) {
      if (!usable(before)) return done('none');
      hadItems = before.item_count > 0;

      var onCart = codesOn(before);
      var others = onCart.filter(function (item) { return has(swaps, item); });
      var staying = onCart.filter(function (item) { return !has(swaps, item) && !same(item, code); });
      var wasOn = has(onCart, code);

      // An empty cart saves nothing, so there is nothing to lose and no
      // way to tell whether a code works. The last code tapped is kept
      // for when something goes in, and no more is said than Copied.
      if (!hadItems) {
        if (wasOn && !others.length) return done('none');
        return settled(write(staying.concat([code]))).then(function () { return done('none'); });
      }

      if (wasOn && !others.length) return done(isWorking(before, code) ? 'already' : 'none');

      // Step 1: the tapped code goes on beside everything already there.
      var adding = wasOn ? Promise.resolve(before) : settled(write(onCart.concat([code])));

      return adding.then(function (both) {
        // Lost or unreadable: the cart is as it was, or it holds the
        // tapped code as well. Neither saves less.
        if (!both) return done('none');

        var held = codesOn(both);
        var dropped = onCart.some(function (item) { return !has(held, item); });
        if (!wasOn && (dropped || both.total_discount < before.total_discount)) {
          return restore(onCart).then(function () { return done('none'); });
        }
        if (!has(held, code)) return done('none');

        // A code that has just gone on is only working if the saving went
        // up. Level means the cart already had its best.
        var raised = wasOn || both.total_discount > before.total_discount;
        var works = raised && isWorking(both, code);
        var othersWorking = others.filter(function (item) { return isWorking(both, item); });

        // Step 2: whichever is doing nothing comes off.
        if (!works) {
          // The tapped code does nothing for this cart, so the cart goes
          // back to exactly what it held.
          var kept = othersWorking[0] || '';
          if (wasOn) return done(kept ? 'keptOther' : 'none', kept);
          return settled(write(onCart)).then(function (after) {
            if (after && kept && !isWorking(after, kept)) return done('none');
            return done(kept ? 'keptOther' : 'none', kept);
          });
        }

        var said = wasOn ? 'already' : 'added';
        // Nothing to take off, or the store is letting both work together,
        // in which case taking one off would cost the customer.
        if (!others.length || othersWorking.length) return done(said);

        return settled(write(staying.concat([code]))).then(function (after) {
          // Lost: both are still held and the tapped one is the one used.
          if (!after) return done(said);
          if (after.total_discount < both.total_discount) {
            return restore(held).then(function () { return done('none'); });
          }
          return done('swapped', others[0]);
        });
      });
    }).catch(function () {
      return done('none');
    }).then(function (answer) {
      answer.redraw = wrote && hadItems;
      return answer;
    });
  }

  function queueApply(state) {
    var config = settings();
    if (!config.apply) return;
    state.applying = true;
    // One at a time. A tap made while another is still running waits its
    // turn, and only the latest tap waits.
    waiting = state;
    applyChain = applyChain.then(function () {
      if (waiting !== state) return null;
      waiting = null;
      return putOnCart(config, state.code, state.swaps);
    }).catch(function () {
      return null;
    }).then(function (answer) {
      var news = !!answer && answer.outcome !== 'none';
      state.applying = false;
      if (news) {
        state.outcome = answer.outcome;
        state.other = answer.other;
      }
      // Not confirmed yet: the copy has still to answer, and it will show
      // and say everything in one go.
      if (current === state && state.shown) {
        if (news) showMessage(state);
        if (!state.spoken) {
          speakOnce(state);
        } else if (news) {
          speak(resultFor(state));
        }
      }
      // The customer has been told. The cart views follow, and the next
      // tap waits for them so two redraws cannot cross.
      return answer && answer.redraw ? redrawCart() : null;
    }).catch(function () {});
  }

  /* ---------- taps ---------- */

  function onTap(event) {
    var target = event.target;
    var control = target && target.closest ? target.closest('[data-promo-code]') : null;
    if (!control) return;
    var code = (control.getAttribute('data-promo-code') || '').trim();
    if (!code) return;

    var state = {
      control: control,
      code: code,
      swaps: listFrom(control.getAttribute('data-promo-swaps')).filter(function (item) { return !same(item, code); }),
      link: control.getAttribute('data-promo-link') || '',
      linkLabel: control.getAttribute('data-promo-link-label') || '',
      copied: false,
      outcome: 'none',
      other: '',
      applying: false,
      shown: false,
      spoken: false,
      speakTimer: null
    };

    copy(code).then(function (copied) {
      state.copied = copied;
      // Only a real copy says Copied. If it failed, the code stays on the
      // control where it can be read.
      if (copied) flash(control);
      current = state;
      state.shown = true;
      showMessage(state);
      if (state.applying) {
        state.speakTimer = setTimeout(function () { speakOnce(state); }, SPEAK_AFTER);
      } else {
        speakOnce(state);
      }
    }).catch(function () {});

    queueApply(state);
  }

  /* ---------- lines that outlive their promotion ---------- */

  function retireEnded() {
    var soonest = 0;
    var now = Date.now();
    Array.prototype.forEach.call(document.querySelectorAll('[data-promo-until]'), function (element) {
      var until = parseInt(element.getAttribute('data-promo-until'), 10);
      if (isNaN(until) || element.hidden) return;
      if (now >= until) {
        element.hidden = true;
      } else if (!soonest || until < soonest) {
        soonest = until;
      }
    });
    return soonest;
  }

  function watchEndings() {
    var soonest = retireEnded();
    if (!soonest) return;
    document.addEventListener('visibilitychange', retireEnded);
    // setTimeout cannot count past about 24.8 days. The visibility check
    // and the next page load cover anything further out.
    var wait = soonest - Date.now();
    if (wait < 2147483647) setTimeout(retireEnded, wait + 50);
  }

  function start() {
    try {
      setUpMessage();
      document.addEventListener('click', onTap);
      watchEndings();
    } catch (error) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
